/**
 * Model Dropdown Component
 *
 * Allows users to select which AI model to use for their messages.
 * Only shows models for providers where the user has configured API keys.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import type { AIModel } from "@/lib/apiKeyStorage";
import { getAvailableModels } from "@/lib/availableModels";
type ModelDropdownProps = {
  selectedModel: AIModel;
  onModelChange: (model: AIModel) => void;
  disabled?: boolean;
  userTier?: "trial" | "pro" | "expired" | "pending";
};
const ModelDropdown: React.FC<ModelDropdownProps> = t0 => {
  const $ = _c(14);
  const {
    selectedModel,
    onModelChange,
    disabled: t1,
    userTier
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const availableModels = getAvailableModels(userTier);
  let t2;
  if ($[0] !== selectedModel) {
    t2 = m => m.value === selectedModel;
    $[0] = selectedModel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selectedModelInfo = availableModels.find(t2);
  let t3;
  let t4;
  if ($[2] !== isOpen) {
    t3 = () => {
      const handleClickOutside = event => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    };
    t4 = [isOpen];
    $[2] = isOpen;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== onModelChange) {
    t5 = model => {
      onModelChange(model);
      setIsOpen(false);
    };
    $[5] = onModelChange;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleModelSelect = t5;
  const openaiModels = availableModels.filter(_temp);
  const claudeModels = availableModels.filter(_temp2);
  const grokModels = availableModels.filter(_temp3);
  const geminiModels = availableModels.filter(_temp4);
  const ssmModels = availableModels.filter(_temp5);
  if (availableModels.length === 0) {
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="text-xs text-foreground/50">No models available. Add API keys in Settings.</div>;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  let t6;
  if ($[8] !== disabled || $[9] !== isOpen) {
    t6 = () => !disabled && setIsOpen(!isOpen);
    $[8] = disabled;
    $[9] = isOpen;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = `h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <svg className={t7} fill="none" stroke="currentColor" viewBox="0 0 24 24">{t8}</svg>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return <div className="relative" ref={dropdownRef}><button type="button" onClick={t6} disabled={disabled} className="flex items-center gap-2 rounded-md border border-foreground/30 bg-white/60 px-3 py-1.5 text-xs text-foreground hover:bg-white/80 disabled:opacity-60 transition-colors" title="Select AI model">{selectedModelInfo?.provider === "openai" ? <span className="flex h-4 w-4 items-center justify-center rounded bg-green-500/30 text-green-700 text-[10px] font-bold">O</span> : selectedModelInfo?.provider === "claude" ? <span className="flex h-4 w-4 items-center justify-center rounded bg-orange-500/30 text-orange-700 text-[10px] font-bold">C</span> : selectedModelInfo?.provider === "gemini" ? <span className="flex h-4 w-4 items-center justify-center rounded bg-purple-500/30 text-purple-700 text-[10px] font-bold">G</span> : selectedModelInfo?.provider === "ssm" ? <span className="flex h-4 w-4 items-center justify-center rounded bg-violet-500/30 text-violet-700 text-[10px] font-bold">S</span> : <span className="flex h-4 w-4 items-center justify-center rounded bg-blue-500/30 text-blue-700 text-[10px] font-bold">X</span>}<span className="font-medium">{selectedModelInfo?.label || selectedModel}</span>{t9}</button>{isOpen && <div className="absolute bottom-full left-0 mb-2 w-80 rounded-lg border border-foreground/20 bg-white/90 backdrop-blur-md shadow-xl z-50 max-h-96 overflow-y-auto"><div className="p-2">{openaiModels.length > 0 && <div className="mb-3"><div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/50">OpenAI Models</div><div className="space-y-1">{openaiModels.map(model_0 => <button key={model_0.value} onClick={() => handleModelSelect(model_0.value)} className={`w-full rounded-md px-3 py-2 text-left transition-colors ${selectedModel === model_0.value ? "bg-green-500/20 border border-green-500/30" : "hover:bg-white/60 border border-transparent"}`}><div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium text-foreground">{model_0.label}</span>{model_0.value.startsWith("gpt-5") && <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] text-emerald-700">Latest</span>}{model_0.value === "gpt-5.1" && <span className="rounded-full bg-purple-500/20 px-1.5 py-0.5 text-[9px] text-purple-700">Recommended</span>}</div><div className="text-[10px] text-foreground/50 leading-tight">{model_0.description}</div></button>)}</div></div>}{claudeModels.length > 0 && <div className="mb-3"><div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/50">Claude Models</div><div className="space-y-1">{claudeModels.map(model_1 => <button key={model_1.value} onClick={() => handleModelSelect(model_1.value)} className={`w-full rounded-md px-3 py-2 text-left transition-colors ${selectedModel === model_1.value ? "bg-orange-500/20 border border-orange-500/30" : "hover:bg-white/60 border border-transparent"}`}><div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium text-foreground">{model_1.label}</span>{(model_1.value === "claude-sonnet-4-5" || model_1.value === "claude-haiku-4-5") && <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] text-emerald-700">Latest</span>}</div><div className="text-[10px] text-foreground/50 leading-tight">{model_1.description}</div></button>)}</div></div>}{grokModels.length > 0 && <div className="mb-3"><div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/50">Grok Models</div><div className="space-y-1">{grokModels.map(model_2 => <button key={model_2.value} onClick={() => handleModelSelect(model_2.value)} className={`w-full rounded-md px-3 py-2 text-left transition-colors ${selectedModel === model_2.value ? "bg-blue-500/20 border border-blue-500/30" : "hover:bg-white/60 border border-transparent"}`}><div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium text-foreground">{model_2.label}</span></div><div className="text-[10px] text-foreground/50 leading-tight">{model_2.description}</div></button>)}</div></div>}{geminiModels.length > 0 && <div className="mb-3"><div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/50">Gemini Models</div><div className="space-y-1">{geminiModels.map(model_3 => <button key={model_3.value} onClick={() => handleModelSelect(model_3.value)} className={`w-full rounded-md px-3 py-2 text-left transition-colors ${selectedModel === model_3.value ? "bg-purple-500/20 border border-purple-500/30" : "hover:bg-white/60 border border-transparent"}`}><div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium text-foreground">{model_3.label}</span>{model_3.value === "gemini-2.0-flash" && <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] text-emerald-700">Latest</span>}</div><div className="text-[10px] text-foreground/50 leading-tight">{model_3.description}</div></button>)}</div></div>}{ssmModels.length > 0 && <div><div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/50">SSM Models (Local)</div><div className="space-y-1">{ssmModels.map(model_4 => <button key={model_4.value} onClick={() => handleModelSelect(model_4.value)} className={`w-full rounded-md px-3 py-2 text-left transition-colors ${selectedModel === model_4.value ? "bg-violet-500/20 border border-violet-500/30" : "hover:bg-white/60 border border-transparent"}`}><div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium text-foreground">{model_4.label}</span><span className="rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[9px] text-violet-700">Local</span></div><div className="text-[10px] text-foreground/50 leading-tight">{model_4.description}</div></button>)}</div></div>}</div></div>}</div>;
};
export default ModelDropdown;
function _temp(m_0) {
  return m_0.provider === "openai";
}
function _temp2(m_1) {
  return m_1.provider === "claude";
}
function _temp3(m_2) {
  return m_2.provider === "grok";
}
function _temp4(m_3) {
  return m_3.provider === "gemini";
}
function _temp5(m_4) {
  return m_4.provider === "ssm";
}